// SPDX-License-Identifier: BUSL-1.1

/**
 * Dashboard V2 System Health pill. Reads a HealthResponse + query error
 * and renders a colored status badge matching the V2 visual language.
 *
 * Shared with DashboardV2's StatusCard; v1 uses the tone-prop Card
 * variant in lib/health.ts::healthToCardProps instead.
 *
 */

import type { HealthResponse } from '@/lib/health';

export function HealthPill({
  health,
  error,
}: {
  health: HealthResponse | undefined;
  error: unknown;
}) {
  if (error) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-950/40 px-2 py-0.5 text-xs font-semibold text-red-400">
        <span className="size-1.5 rounded-full bg-red-400" />
        UNREACHABLE
      </span>
    );
  }
  if (!health) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-800 px-2 py-0.5 text-xs font-semibold text-neutral-400">
        <span className="size-1.5 rounded-full bg-neutral-500" />
        CHECKING
      </span>
    );
  }
  if (health.status === 'ok') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-950/40 px-2 py-0.5 text-xs font-semibold text-green-400">
        <span className="size-1.5 rounded-full bg-green-400" />
        HEALTHY
      </span>
    );
  }
  if (health.status === 'degraded') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-950/40 px-2 py-0.5 text-xs font-semibold text-yellow-400">
        <span className="size-1.5 rounded-full bg-yellow-400" />
        DEGRADED
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-red-950/40 px-2 py-0.5 text-xs font-semibold text-red-400">
      <span className="size-1.5 rounded-full bg-red-400" />
      UNHEALTHY
    </span>
  );
}
